/* eslint-disable react/prop-types */
import React from 'react';
import InstallationInfoCard from './InstallationInfoCard';
import InstallationPlatformSplit from './InstallationPlatformSplit';

/**
 * Selected-period installation card (feature 031, US1 counts / US3 deltas).
 *
 * ONE card — "Installs (Selected date range)" — carrying the range total with Android
 * and iOS on the SAME line, in the same `inst-line` row as the all-time card above, so the
 * page reads as the same shape twice: one figure, split by platform. With the brackets it
 * is the widest row on the page, so it wraps rather than overflowing on a narrow card.
 *
 * Each delta sits in BRACKETS straight after the count it qualifies, rather than on a
 * line of its own: a percentage change means nothing without the number it is a change
 * in, and reading them together is the whole point of the card. It is still conveyed by
 * TEXT + ICON together, never by colour alone.
 *
 * The window itself, and the prior window it is compared against, live behind the card's
 * `i` button rather than in a caption above the row — the dates are reference, not the
 * headline, and they were repeating what the date filter already shows.
 *
 * The backend (installation-summary) encodes a zero prior window as a null delta, so we
 * never divide client-side — and we show NO chip at all in that case. There was nothing
 * in the prior window to compare against, so any percentage would be invented; a bare
 * count says exactly that much and no more.
 *
 * Props: { period }  — { from_date, to_date, prior_from_date, prior_to_date,
 *                        installs, android_installs, ios_installs,
 *                        delta_installs_pct, delta_android_pct, delta_ios_pct }
 */
const n = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString());

const inclusiveDays = (from, to) => {
  if (!from || !to) return 0;
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.round((b - a) / 86400000) + 1;
};

/**
 * Map a delta percentage to an accessible {icon, text, sr} triple, or NULL when there is
 * no comparison to draw — `pct === null` is the server-encoded zero-prior-window case.
 * Never produces NaN/Infinity.
 */
export const deltaLabel = (pct, priorDays) => {
  const suffix = `vs prior ${priorDays} day${priorDays === 1 ? '' : 's'}`;
  if (pct === null || pct === undefined) return null;
  if (pct > 0) return { icon: '▲', text: `${pct}% ${suffix}`, sr: `up ${pct}% ${suffix}` };
  if (pct < 0) return { icon: '▼', text: `${Math.abs(pct)}% ${suffix}`, sr: `down ${Math.abs(pct)}% ${suffix}` };
  return { icon: '—', text: `0% ${suffix}`, sr: `no change, 0% ${suffix}` };
};

// Bracketed and inline, so it is read as a qualifier on the count right before it.
const Delta = ({ pct, priorDays }) => {
  const d = deltaLabel(pct, priorDays);
  if (!d) return null;                       // nothing to compare against — show the count alone
  const { icon, text, sr } = d;
  return (
    // A real space, not a CSS margin, so the figure and its qualifier stay readable
    // when the card is copied out or read aloud.
    <span className="inst-delta" aria-label={sr}>
      {' ('}<i className="inst-delta__icon" aria-hidden="true">{icon}</i>
      <span>{text}</span>)
    </span>
  );
};

const InstallationPeriodCards = ({ period }) => {
  if (!period) return null;
  const priorDays = inclusiveDays(period.from_date, period.to_date);

  return (
    <div className="au-cards inst-cards inst-cards--period" data-testid="inst-period">
      <InstallationInfoCard
        id="period"
        title="Installs (Selected date range)"
        note={(
          <>Installs whose <b>install date (UTC)</b> falls in the selected range,
            {' '}<b>{period.from_date}</b> → <b>{period.to_date}</b>, split by device
            platform. Each bracket compares that figure with the equally long window
            immediately before it, {period.prior_from_date} → {period.prior_to_date}.</>
        )}
      >
        <div className="inst-line" data-testid="inst-period-line">
          <span className="au-card__value inst-num" data-testid="inst-period-total">
            {n(period.installs)}
            <Delta pct={period.delta_installs_pct} priorDays={priorDays} />
          </span>
          <InstallationPlatformSplit
            inline
            testId="inst-period-platform"
            items={[
              {
                label: 'Android',
                testId: 'inst-period-android',
                value: n(period.android_installs),
                extra: <Delta pct={period.delta_android_pct} priorDays={priorDays} />,
              },
              {
                label: 'iOS',
                testId: 'inst-period-ios',
                value: n(period.ios_installs),
                extra: <Delta pct={period.delta_ios_pct} priorDays={priorDays} />,
              },
            ]}
          />
        </div>
      </InstallationInfoCard>
    </div>
  );
};

export default InstallationPeriodCards;
